import React from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../theme";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";

export default function GenderPieChart({ patients }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const COLORS = [colors.blueAccent[400], colors.greenAccent[400]]; // Male, Female

  const genderCount = patients?.reduce(
    (acc, patient) => {
      const gender = patient.gender?.toLowerCase();
      if (gender === "male") acc[0].value += 1;
      else if (gender === "female") acc[1].value += 1;
      return acc;
    },
    [
      { name: "Male", value: 0 },
      { name: "Female", value: 0 },
    ]
  );

  const total = genderCount.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          sx={{
            backgroundColor: colors.primary[400],
            border: `2px solid ${colors.blueAccent[500]}`,
            borderRadius: '12px',
            p: 2,
            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color: colors.grey[100] }}>
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[300] }}>
            Count: {data.value}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.greenAccent[400], fontWeight: 600 }}>
            {((data.value / total) * 100).toFixed(1)}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ 
          fontSize: '16px', 
          fontWeight: 700,
          textShadow: '0 2px 4px rgba(0,0,0,0.5)'
        }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Box>
      {/* Stats Summary */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 4, 
          mb: 2,
          px: 3 
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: `${colors.blueAccent[800]}30`,
            border: `1px solid ${colors.blueAccent[600]}40`,
          }}
        >
          <MaleIcon sx={{ fontSize: 28, color: colors.blueAccent[400] }} />
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
              Male
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.blueAccent[300] }}>
              {genderCount[0].value}
            </Typography>
          </Box>
        </Box>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: `${colors.greenAccent[800]}30`,
            border: `1px solid ${colors.greenAccent[600]}40`,
          }}
        >
          <FemaleIcon sx={{ fontSize: 28, color: colors.greenAccent[400] }} />
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
              Female
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.greenAccent[300] }}>
              {genderCount[1].value}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Pie Chart */}
      <Box height={300} p={2}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={genderCount}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={100}
              innerRadius={60}
              dataKey="value"
              paddingAngle={2}
              animationBegin={0}
              animationDuration={800}
            >
              {genderCount.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS[index]}
                  strokeWidth={2}
                  stroke={colors.primary[400]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Total Patients */}
      <Box sx={{ textAlign: 'center', mt: -2 }}>
        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
          Total Patients
        </Typography>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            color: colors.grey[100],
            background: `linear-gradient(135deg, ${colors.blueAccent[300]} 0%, ${colors.greenAccent[400]} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {total}
        </Typography>
      </Box>
    </Box>
  );
}
